import type { SurveyModel } from 'survey-core';

export type SurveyNavigationState = {
  currentElementName?: string;
  currentPageNo: number;
  isFirstStep: boolean;
  isLastStep: boolean;
};

type PersistedSurveyNavigation = {
  currentElementName?: unknown;
  currentPageNo?: unknown;
};

export const getSurveyNavigationState = (survey?: SurveyModel): SurveyNavigationState => ({
  currentElementName: survey?.currentElementName || undefined,
  currentPageNo: survey?.currentPageNo ?? 0,
  isFirstStep: survey?.isFirstPage ?? true,
  isLastStep: survey?.isLastPage ?? true,
});

export const advanceSurvey = (survey: SurveyModel): boolean => (
  survey.isLastPage ? survey.completeLastPage() : survey.nextPage()
);

/**
 * SurveyJS 2.x keeps question-per-page elements on their source page, so its
 * page-based progress remains at zero while moving between questions/panels.
 * Recreate the legacy virtual-page progress from the active top-level element.
 */
export const getSurveyProgress = (survey?: SurveyModel): number => {
  if (!survey || survey.questionsOnPageMode !== 'questionPerPage') {
    return survey?.getProgress() ?? 0;
  }

  const currentElement = survey.currentElement;
  const visibleElements = survey.visiblePages.flatMap((page) =>
    page.elements.filter((element) => element === currentElement || element.isVisible)
  );
  const currentIndex = currentElement ? visibleElements.indexOf(currentElement) : -1;

  return currentIndex >= 0 && visibleElements.length > 0
    ? Math.ceil((currentIndex * 100) / visibleElements.length)
    : survey.getProgress();
};

export const restoreSurveyNavigation = (
  survey: SurveyModel,
  persisted?: PersistedSurveyNavigation,
): SurveyNavigationState => {
  const currentElementName = typeof persisted?.currentElementName === 'string'
    ? persisted.currentElementName
    : undefined;
  const currentElement = currentElementName
    ? survey.getElementByName(currentElementName)
    : undefined;

  if (currentElement && currentElement.isVisible) {
    survey.currentElementName = currentElementName!;
  } else {
    const requestedPageNo = typeof persisted?.currentPageNo === 'number'
      && !Number.isNaN(persisted.currentPageNo)
      ? persisted.currentPageNo
      : 0;
    const lastPageNo = Math.max((survey.visiblePageCount ?? 1) - 1, 0);
    survey.currentPageNo = Math.min(Math.max(requestedPageNo, 0), lastPageNo);
  }

  return getSurveyNavigationState(survey);
};
